/**
 * Custom Loader Component
 * Shows DSL loader with custom progress (not tied to drei's useProgress)
 */

import React, { useRef, useEffect, useState } from 'react'
import { getMediaQueryKey } from '../utils/deviceDetection'

// Base (1080p/Large) loader dimensions. Each tier scales these to ACTUAL pixel
// sizes (not a CSS transform) so the loader renders crisply. 4K = 4× the base;
// tablet landscape = ⅓ of 4K (= 4/3× the base).
const BASE = {
  // Logo width tuned so the VISIBLE mark matches the visible width of the motion
  // graphic (the logo image has transparent padding, so its box is a bit narrower
  // than the graphic's to make the rendered widths line up).
  logo: 192,
  // Gap between the bottom of the logo and the top of the motion graphic. The logo
  // is now stacked directly above the animation (not pinned to the viewport top),
  // so it stays a few px above the graphic at any viewport height. One pre-scale
  // value → each tier renders it at logoGap × its own scale (tablet ×4/3, 4K ×4).
  logoGap: 12,
  // Animation + bar + text increased 50% from the reduced sizes (170 / 128 / 2
  // / 8 / 6.5).
  graphic: 255, // Artemis II motion graphic width (height auto, keeps aspect)
  barWidth: 192,
  barHeight: 4,
  fontSize: 12,
  gap: 9.75, // ~0.8em at the font size
}

// Loader scaling follows the project's 4K-is-gospel rule (see DSL_Socials): every
// tier is a fraction of the 4K size, applied as `BASE × scale`.
//   4K   = 4× base            (gospel)
//   1440 = ⅔ of 4K = 8/3 base
//   1080 = ½ of 4K = 2  base
//   tablet = ⅓ of 4K = 4/3 base
// Anything else (mobile) keeps the base 1×.
function loaderScale() {
  const key = getMediaQueryKey()
  if (key === 'desktop4k') return 4
  if (key === 'desktop1440') return 8 / 3
  if (key === 'desktop1080') return 2
  if (key === 'tabletLandscape') return 4 / 3
  return 1
}

export function CustomLoader({ progress, dataInterpolation }) {
  const progressRef = useRef(0)
  const rafRef = useRef(0)
  const progressSpanRef = useRef(null)
  const [shown, setShown] = useState(true)
  const isMountedRef = useRef(true)
  const [scale, setScale] = useState(() => (typeof window !== 'undefined' ? loaderScale() : 1))

  // Track viewport so the loader picks the right ACTUAL dimensions if the window
  // is resized (or the breakpoint changes) while loading.
  useEffect(() => {
    const onResize = () => setScale(loaderScale())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const updateProgress = React.useCallback(() => {
    if (!progressSpanRef.current || !isMountedRef.current) return
    progressRef.current += (progress - progressRef.current) / 2
    if (progressRef.current > 0.95 * progress || progress === 100) progressRef.current = progress
    progressSpanRef.current.innerText = dataInterpolation(progressRef.current)
    if (progressRef.current < progress && isMountedRef.current) {
      rafRef.current = requestAnimationFrame(updateProgress)
    }
  }, [dataInterpolation, progress])

  useEffect(() => {
    isMountedRef.current = true
    updateProgress()
    return () => {
      isMountedRef.current = false
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = 0
      }
    }
  }, [updateProgress])

  // Hide loader when progress is 100
  useEffect(() => {
    let timer = null
    
    if (progress >= 100) {
      timer = setTimeout(() => {
        if (isMountedRef.current) {
          setShown(false)
        }
      }, 500)
    }
    
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [progress])

  if (!shown) return null

  // Actual per-breakpoint dimensions (not a CSS transform); see loaderScale.
  const logo = BASE.logo * scale
  const graphic = BASE.graphic * scale
  const barWidth = BASE.barWidth * scale
  const barHeight = BASE.barHeight * scale
  const fontSize = BASE.fontSize * scale
  const gap = BASE.gap * scale
  const logoGap = BASE.logoGap * scale

  return (
    <div style={styles.container}>
      {/* Logo + motion graphic + loading bar — centred in the screen as one stack,
          so the logo is anchored a few px above the animation rather than to the
          top of the viewport (keeps the two together at any viewport height). */}
      <div style={styles.stack}>
        {/* DSL logo — sits directly above the animation, horizontally centred. */}
        <img
          src="./images/dslBrand/DSL Logo Light and Orange.png"
          style={{ width: `${logo}px`, display: 'block', marginBottom: logoGap }}
          alt="DSL Logo"
        />
        <div style={styles.image}>
          <center>
            <video
              src="./media/ArtemisIIMotionGraphic.webm"
              style={{ width: `${graphic}px`, height: 'auto', display: 'block' }}
              autoPlay
              loop
              muted
              playsInline
            />
          </center>
        </div>
        <div style={{ ...styles.inner, width: barWidth, height: barHeight, marginTop: gap }}>
          <div style={{ ...styles.bar, height: barHeight, transform: `scaleX(${progress / 100})` }}></div>
          <span ref={progressSpanRef} style={{ ...styles.data, fontSize, marginTop: gap }} />
        </div>
      </div>
    </div>
  )
}

const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#000000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 500ms ease',
    zIndex: 2147483647,
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  inner: {
    background: '#303030',
    textAlign: 'center',
  },
  bar: {
    width: '100%',
    background: '#ed1b30',
    transition: 'transform 200ms',
    transformOrigin: 'left center',
  },
  data: {
    display: 'inline-block',
    position: 'relative',
    fontVariantNumeric: 'tabular-nums',
    color: '#f0f0f0',
    fontFamily: '"Public Sans", sans-serif',
    whiteSpace: 'nowrap',
  },
  image: {
    display: 'block',
  },
}

export default CustomLoader
